import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from '../../../../../components/form/FormGroup';
import { Col, Row, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ExtraFieldsSupportedObject } from '../../../components/ExtraFields';
import { ExtraFieldsFormId } from '../../../components/ExtraFields/types';
import { useCurrencyFormatter } from '../../../../../hooks/useCurrencyFormatter';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import { ExtraFieldsViewAreaContent } from '../../../components/ExtraFields/ExtraFieldsViewArea/ExtraFieldsViewAreaContent';
import { ExpenseItem } from '../../../__generated__/graphql';
import { useObjectExtraFields } from '../../../components/ExtraFields/useObjectExtraFields';
import { mapToExtraFieldInternalValue } from '../../../components/ExtraFields/extraFieldInternalValue';

type Props = {
  items: ExpenseItem[];
  currencyIsoCode?: string;
};

const EMPTY_VALUE = '---';

const ExpenseItemsOverview: React.FC<React.PropsWithChildren<Props>> = ({
  items,
  currencyIsoCode,
}) => {
  const { t } = useTranslation();
  const { getFormFields } = useObjectExtraFields(
    ExtraFieldsSupportedObject.ExpenseItem,
  );
  const formFields = getFormFields(
    ExtraFieldsFormId.ExpensesRequestExpenseItem,
  );

  const formatCurrency = useCurrencyFormatter();

  if (!items || items.length === 0) {
    return <p>{t('expenses.expenseSideBar.noItems')}</p>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {items.map((item) => (
        <div key={item.Id} className="border rounded p-3">
          <Row className="mb-3">
            <Col>
              <FormGroup>
                <Form.Label className="fw-bold">
                  {t('expenses.expenseSideBar.merchant')}
                </Form.Label>
                <p>{item.merchant?.Name ?? EMPTY_VALUE}</p>
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <FormGroup>
                <Form.Label className="fw-bold">
                  {t('expenses.expenseSideBar.itemManualAmount')}
                </Form.Label>
                <p>
                  {item.flair__Manual_Amount__c != null && currencyIsoCode
                    ? formatCurrency({
                        value: item.flair__Manual_Amount__c,
                        currency: currencyIsoCode,
                      })
                    : EMPTY_VALUE}
                </p>
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <div className="d-flex align-items-center gap-1">
                  <Form.Label className="fw-bold">
                    {t('expenses.expenseSideBar.itemFinalAmount')}
                  </Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="item-final-amount-tooltip">
                        {t('expenses.expenseSideBar.itemFinalAmountTooltip')}
                      </Tooltip>
                    }>
                    <span className="ms-1 mt-n1">
                      <FlairIcon icon="information-circle-outline" size="sm" />
                    </span>
                  </OverlayTrigger>
                </div>
                <p>
                  {item.flair__Amount__c != null && currencyIsoCode
                    ? formatCurrency({
                        value: item.flair__Amount__c,
                        currency: currencyIsoCode,
                      })
                    : EMPTY_VALUE}
                </p>
              </FormGroup>
            </Col>
          </Row>
          <ExtraFieldsViewAreaContent
            values={item.extraFields.map(mapToExtraFieldInternalValue)}
            formFields={formFields}
            recordType={item.recordType}
            columnsCount={2}
          />
          {item.ContentDocumentLinks &&
            item.ContentDocumentLinks.length > 0 && (
              <Row className="mt-3">
                <Col>
                  <FormGroup>
                    <Form.Label className="fw-bold">
                      {t('expenses.expenseSideBar.invoiceAttached')}
                    </Form.Label>
                    {item.ContentDocumentLinks.map((document) => (
                      <div key={document.Id}>
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={document.downloadUrl}>
                          {document.ContentDocument.FileExtension
                            ? `${document.ContentDocument.Title}.${document.ContentDocument.FileExtension}`
                            : document.ContentDocument.Title}
                          <span className="ms-2">
                            <FlairIcon size="xs" icon="external-link-outline" />
                          </span>
                        </a>
                      </div>
                    ))}
                  </FormGroup>
                </Col>
              </Row>
            )}
        </div>
      ))}
    </div>
  );
};
export default ExpenseItemsOverview;
